import React, { useState, useEffect, useCallback } from 'react';
import { litterService } from '../services/litterApi';
import { LitterForm } from './LitterForm';
import { BREEDS, BUSINESS_INFO } from '../data/mock';
import { Plus, Baby, Calendar, MessageCircle, Loader2, Trash2, Edit3 } from 'lucide-react';

export const LittersSection = ({ isAdmin }) => {
  const [litters, setLitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLitter, setEditingLitter] = useState(null);

  const loadLitters = useCallback(async () => {
    try {
      const data = await litterService.getAll();
      setLitters(data || []);
    } catch {
      setLitters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLitters();
  }, [loadLitters]);

  const handleSave = async (data) => {
    try {
      if (editingLitter) {
        const updated = await litterService.update(data.id, data);
        setLitters(litters.map(l => l.id === data.id ? updated : l));
      } else {
        const created = await litterService.create(data);
        setLitters([...litters, created]);
      }
      setEditingLitter(null);
    } catch {
      alert('Error al guardar la camada');
    }
  };

  const handleDelete = async (id) => {
    try {
      await litterService.delete(id);
      setLitters(litters.filter(l => l.id !== id));
    } catch {
      alert('Error al eliminar la camada');
    }
  };

  const handleWhatsAppClick = (litter) => {
    const breed = BREEDS.find(b => b.id === litter.breed);
    const message = encodeURIComponent(
      `¡Hola! Me interesan las próximas camadas de ${breed?.name || litter.breed}. ¿Podrían darme más información?`
    );
    window.open(`https://wa.me/34${BUSINESS_INFO.whatsapp}?text=${message}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const months = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  // Don't show section if no litters and not admin
  if (!isAdmin && litters.length === 0 && !loading) return null;

  return (
    <section data-testid="litters-section" className="bg-gradient-to-br from-pink-50 via-rose-50 to-amber-50 rounded-3xl p-8 mb-10 border-2 border-pink-200">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-800 px-4 py-2 rounded-full mb-4">
          <Baby className="w-5 h-5" />
          <span className="font-semibold">Reserva anticipada</span>
        </div>
        <h2 className="text-3xl font-bold text-pink-900 mb-2">
          Próximas Camadas
        </h2>
        <p className="text-pink-700">
          Conoce nuestras próximas camadas y reserva tu cachorro antes de que nazca
        </p>
      </div>

      {/* Admin Add Button */}
      {isAdmin && (
        <div className="mb-6">
          <button
            data-testid="add-litter-btn"
            onClick={() => { setEditingLitter(null); setIsFormOpen(true); }}
            className="flex items-center gap-2 bg-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            Anunciar Camada
          </button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-10 h-10 animate-spin text-pink-500 mx-auto mb-3" />
          <p className="text-pink-600">Cargando camadas...</p>
        </div>
      ) : litters.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {litters.map(litter => {
            const breed = BREEDS.find(b => b.id === litter.breed);
            return (
              <div key={litter.id} data-testid={`litter-card-${litter.id}`} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-pink-100">
                {/* Parents photos */}
                <div className="grid grid-cols-2 gap-px bg-pink-100">
                  <div className="relative">
                    <img
                      src={litter.fatherImage || 'https://via.placeholder.com/300x200?text=Papa'}
                      alt="Papa"
                      className="w-full h-40 object-cover"
                    />
                    <span className="absolute bottom-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      Papa
                    </span>
                  </div>
                  <div className="relative">
                    <img
                      src={litter.motherImage || 'https://via.placeholder.com/300x200?text=Mama'}
                      alt="Mama"
                      className="w-full h-40 object-cover"
                    />
                    <span className="absolute bottom-2 right-2 bg-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      Mama
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{breed?.icon}</span>
                    <h3 className="text-lg font-bold text-pink-900">{breed?.name || litter.breed}</h3>
                  </div>

                  <div className="flex items-center gap-2 text-pink-700 mb-3 bg-pink-50 rounded-lg px-3 py-2">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">Nacimiento previsto: {formatDate(litter.expectedDate)}</span>
                  </div>

                  {litter.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{litter.description}</p>
                  )}

                  {/* Actions */}
                  {isAdmin ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingLitter(litter); setIsFormOpen(true); }}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-pink-100 text-pink-800 rounded-xl font-medium hover:bg-pink-200 transition-all"
                      >
                        <Edit3 className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => { if (window.confirm('¿Eliminar esta camada?')) handleDelete(litter.id); }}
                        className="py-2.5 px-4 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleWhatsAppClick(litter)}
                      className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-600 hover:shadow-lg transition-all duration-300"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Reservar / Mas info
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : isAdmin ? (
        <div className="text-center py-12 bg-white/50 rounded-2xl">
          <Baby className="w-12 h-12 mx-auto text-pink-300 mb-3" />
          <h3 className="text-lg font-semibold text-pink-800 mb-1">
            No hay camadas anunciadas
          </h3>
          <p className="text-pink-600 text-sm">
            Anuncia tus proximas camadas con el boton de arriba
          </p>
        </div>
      ) : null}

      {/* Litter Form Modal */}
      <LitterForm
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingLitter(null); }}
        onSave={handleSave}
        editingLitter={editingLitter}
      />
    </section>
  );
};
